import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import type {
  LotteryCity,
  LotteryFilter,
  LotteryData,
  Lottery,
  LotteryPrize
} from '~/composables/lobby-games/interface'
import { DATE_TIME_FORMAT, LOTTERY_CITY, LOTTERY_RESULTS } from '~/constants'
import { LODE_TABS } from '~/resources/lobby-games'
import { useLuckyNumberStore } from '~/stores/lode-lucky-number.store.ts'
dayjs.extend(customParseFormat)

export function useLode() {
  const runtimeConfig = useRuntimeConfig()
  const baseURLLottery = runtimeConfig.public.DOMAIN_LOTTERY_RESULT
  const isLoading = ref(false)
  const currentDate = new Date()
  const THRESHOLD_HOUR = 19 // 7:00 pm
  if (currentDate?.getHours() < THRESHOLD_HOUR) {
    currentDate.setDate(currentDate.getDate() - 1)
  }

  const tabNumber = ref(0)
  const probeNumber = ref('')
  const regions = reactive<LotteryCity[]>([])
  const lotteryFilter = reactive<LotteryFilter>({
    region: regions[0] ?? null,
    date: currentDate
  })
  const lotteryData = reactive<LotteryData>({
    originalResult: {},
    lottery: {
      title: '',
      result: {}
    }
  })

  const luckyNumber = ref()
  const userLuckyNumberStore = useLuckyNumberStore()

  const changeDate = (value: Date): void => {
    if (value) {
      lotteryFilter.date = value
      void fetchCities(dayjs(value).format(DATE_TIME_FORMAT.SHORT_DATE_FORMAT))
    }
  }

  const fetchCities = async (date?: string): Promise<void> => {
    const newDate = date ?? dayjs().format(DATE_TIME_FORMAT.SHORT_DATE_FORMAT)
    try {
      isLoading.value = true
      const data = await $fetch(baseURLLottery + LOTTERY_CITY, {
        query: {
          date: newDate
        }
      })
      const responseData = typeof data === 'string' ? JSON.parse(data) : data

      if (responseData != null) {
        const newData =
          responseData?.rows?.map((item: LotteryCity) => ({
            ...item,
            code: item.id,
            label: item.name,
            value: item.region
          })) ?? []
        regions.splice(0)
        regions.push(...newData)
        lotteryFilter.region = regions?.[0]
        await getLotteryData(lotteryFilter.date, regions?.[0])
        isLoading.value = false
      }
    } finally {
      isLoading.value = false
    }
  }

  const fetchLotteryResult = async (city: number, date?: string): Promise<Lottery> => {
    const newDate = date ?? dayjs().format(DATE_TIME_FORMAT.SHORT_DATE_FORMAT)
    try {
      const data = await $fetch(baseURLLottery + LOTTERY_RESULTS, {
        immediate: true,
        server: true,
        query: {
          date: newDate,
          city
        }
      })
      const responseData = typeof data === 'string' ? JSON.parse(data) : data
      return responseData ? responseData?.rows : { title: '', result: {} }
    } catch {
      return {
        title: '',
        result: {}
      }
    }
  }

  const getLotteryData = async (
    date: Date | null = lotteryFilter.date,
    region: LotteryCity | null = regions[0]
  ): Promise<void> => {
    const newDate = dayjs(date).format(DATE_TIME_FORMAT.SHORT_DATE_FORMAT)
    const data = region?.id ? await fetchLotteryResult(region.id, newDate) : { title: '', result: {} }
    lotteryData.lottery = data
    lotteryData.originalResult = data.result
    changeSearchNumberTab(tabNumber.value)
  }

  const changeSearchNumberTab = (tabValue: number): void => {
    let newResult: LotteryPrize = {}
    Object.entries(lotteryData.originalResult).forEach(item => {
      if (item.length >= 2) {
        let newValue = item[1]
        if (item[0].includes('prize') || item[0] === 'special') {
          const numberData = item[1]?.toString()?.split('-')
          newValue =
            numberData
              ?.map(it => {
                if (!tabValue) {
                  return it.trim()
                }
                return tabValue > it.trim().length ? '' : it.trim().slice(-tabValue)
              })
              .filter(it => it)
              .join(' - ') ?? ''
        }
        newResult = {
          ...newResult,
          [item[0]]: item[1] && !newValue ? null : newValue
        }
      }
    })
    tabNumber.value = tabValue
    lotteryData.lottery.result = newResult
  }

  const formatLotteryData = (value: string, probeNumber: string): string => {
    const numbers = value.split('-')
    const data = numbers.map((item: string) => {
      item = item.trim()
      if (probeNumber) {
        const regex = `(${probeNumber})(?!\\d)`
        item = item.replace(new RegExp(regex, 'g'), match => `<span class="bg-hightlight">${match}</span>`)
      }
      return item
    })
    return data.join('&nbsp;	- &nbsp;')
  }

  function convertStringSpecial(input: string): string {
    let result = ''

    for (let i = 0; i < input.length; i++) {
      const char = input[i]
      result += /\d/.test(char) ? `<p>${char}</p>` : char
    }

    return result
  }

  const formatLotteryDataSpecial = (value: string, probeNumber: string): string => {
    const numbers = value.replace(/<\/?p>/g, '').split('-')

    const data = numbers.map((item: string) => {
      item = item.trim()
      if (probeNumber) {
        const regex = `(${probeNumber})(?!\\d)`
        item = item.replace(new RegExp(regex, 'g'), match => `<span class="bg-hightlight">${match}</span>`)
        return new RegExp(regex).test(item) ? convertStringSpecial(item) : StringUtils.splitStringAndWrap(item, 'p')
      }
      return '<p>' + item + '</p>'
    })
    return data.join('')
  }

  const guessNumbers = (value: string | null | number, isSpecial: boolean = false): string => {
    if (!value) {
      return '-'
    }
    if (!probeNumber.value) {
      return value.toString().split('-').join(' - ')
    }

    return probeNumber.value && isSpecial
      ? formatLotteryDataSpecial(value.toString(), probeNumber.value)
      : formatLotteryData(value.toString(), probeNumber.value)
  }

  const generateTwoRandomDigits = (): string => {
    const number = Math.floor(Math.random() * 100)
    return number.toString().padStart(2, '0')
  }

  const resetNumberDaily = (): void => {
    const currentDate = new Date()
    const lastResetDate = userLuckyNumberStore.getLastResetDate() ?? 0
    luckyNumber.value = userLuckyNumberStore.getLuckyNumber()
    if (currentDate.getDate() !== lastResetDate) {
      luckyNumber.value = generateTwoRandomDigits()
      userLuckyNumberStore.setLuckyNumber(luckyNumber.value)
      userLuckyNumberStore.setLastResetDate(currentDate.getDate())
    }
  }

  const filterTextLottery = (value: string): string => {
    return value
      .replace(/ ngày/g, ', ngày')
      .replace(/-/g, '/')
      .replace(/ chưa có kết quả/g, '')
  }

  const changeRegion = (item: LotteryCity): void => {
    lotteryFilter.region = item
    void getLotteryData(lotteryFilter.date, item)
  }

  watch(
    () => lotteryFilter.date,
    value => {
      void fetchCities(dayjs(value).format(DATE_TIME_FORMAT.SHORT_DATE_FORMAT))
    },
    {
      immediate: true
    }
  )

  return {
    currentDate,
    changeDate,
    lotteryFilter,
    isLoading,
    regions,
    lotteryData,
    guessNumbers,
    LODE_TABS,
    tabNumber,
    resetNumberDaily,
    luckyNumber,
    filterTextLottery,
    changeRegion,
    probeNumber,
    changeSearchNumberTab
  }
}
